import { useThree } from '@react-three/fiber'
import { useEffect, useRef, useCallback } from 'react'
import { useClickStore } from '../store/clickStore'
import { useShallow } from 'zustand/react/shallow'
import TWEEN from 'tween'
import * as THREE from 'three'
import { getSite } from '../config/siteConfig'

function defaultCameraSettings() {
  const cam = getSite().defaultCamera || {};
  const pos = cam.position || [0, 1500, 2000];
  const look = cam.lookAt || [-50, -250, -163.69];
  return {
    camPos: { x: pos[0], y: pos[1], z: pos[2] },
    lookAt: { x: look[0], y: look[1], z: look[2] },
  };
}

export default function CameraControl({ controls }) {
    const camera = useThree((s) => s.camera);
    const invalidate = useThree((s) => s.invalidate);
    const animationIdRef = useRef(null);
    const isFlyAroundActiveRef = useRef(false);

    const { zoomPosition, zoomToExtent, setZoomToExtent, isFlyAroundActive } = useClickStore(
        useShallow((state) => ({
            zoomPosition: state.zoomPosition,
            zoomToExtent: state.zoomToExtent,
            setZoomToExtent: state.setZoomToExtent,
            isFlyAroundActive: state.isFlyAroundActive,
        }))
    );

    // Update ref when state changes
    useEffect(() => {
        isFlyAroundActiveRef.current = isFlyAroundActive;
    }, [isFlyAroundActive]);

    useEffect(() => {
        if (zoomToExtent) {
            zoomToDefault(controls, camera);
            setZoomToExtent(false);
            invalidate();
        }
    }, [zoomToExtent, controls, camera, setZoomToExtent, invalidate]);

    useEffect(() => {
        if (zoomPosition) {
            // First, look at the target point
            createTween(controls.current.target, zoomPosition, 800);
            
            // Then, after a short delay, move closer to the point
            setTimeout(() => {
                const newCameraPosition = calculateOptimalCameraPosition(camera.position, zoomPosition);
                createTween(camera.position, newCameraPosition, 1200);
                invalidate();
            }, 400);
            invalidate();
        }
    }, [zoomPosition, camera, controls, invalidate]);


    const stopFlyAroundAnimation = useCallback(() => {
        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current);
            animationIdRef.current = null;
        }
    }, []);

    // Fly around animation effect
    // This creates a smooth circular orbit around the scene center with dynamic elevation
    // The camera maintains focus on the center point while moving in a circular path
    // Elevation changes create a 3D spiral pattern that goes above and below the center
    const startFlyAroundAnimation = useCallback(() => {
        if (animationIdRef.current) return; // Already animating
        
        // Use the current camera position to determine a good orbit center
        const currentPos = camera.position;
        const { lookAt } = defaultCameraSettings();
        const center = new THREE.Vector3(lookAt.x, lookAt.y, lookAt.z);
        const radius = Math.max(1500, currentPos.distanceTo(center)); // Dynamic radius based on current distance
        const baseHeight = center.y; // Base height at center level
        const speed = 0.0015; // Slower, smoother rotation speed
        const elevationRange = 1200; // How much above and below center to fly
        
        let angle = Math.atan2(currentPos.z - center.z, currentPos.x - center.x); // Start from current angle
        
        const animate = () => {
            if (!isFlyAroundActiveRef.current) return;
            
            angle += speed;
            
            // Calculate camera position in circular orbit
            const x = center.x + radius * Math.cos(angle);
            const z = center.z + radius * Math.sin(angle);
            
            // Create dynamic elevation that goes both above and below center
            // Use multiple sine waves for more interesting elevation patterns
            const elevation1 = elevationRange * Math.sin(angle * 0.4); // Primary elevation wave
            const elevation2 = elevationRange * 0.3 * Math.sin(angle * 1.2); // Secondary wave for variation
            const elevation3 = elevationRange * 0.2 * Math.sin(angle * 2.1); // Tertiary wave for complexity
            
            const y = baseHeight + elevation1 + elevation2 + elevation3;
            
            // Smoothly move camera
            camera.position.lerp(new THREE.Vector3(x, y, z), 0.05);
            
            // Always look at center
            camera.lookAt(center);
            
            // Update controls target
            if (controls.current) {
                controls.current.target.copy(center);
                controls.current.update();
            }

            invalidate();
            animationIdRef.current = requestAnimationFrame(animate);
        };
        
        animate();
    }, [camera, controls, invalidate]);

    useEffect(() => {
        if (isFlyAroundActive) {
            startFlyAroundAnimation();
        } else {
            stopFlyAroundAnimation();
        }
    }, [isFlyAroundActive, startFlyAroundAnimation, stopFlyAroundAnimation]);

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            stopFlyAroundAnimation();
        };
    }, [stopFlyAroundAnimation]);

    return null;
}

function calculateOptimalCameraPosition(currentPosition, targetPosition) {
    // Calculate the direction from camera to target
    const direction = new THREE.Vector3()
        .subVectors(targetPosition, currentPosition)
        .normalize();
    
    // Calculate distance from current position to target
    const distance = currentPosition.distanceTo(targetPosition);
    
    // Define optimal zoom distance (adjust this value as needed)
    const optimalDistance = Math.min(distance * 0.3, 500); // 30% of current distance, max 500 units
    
    // Calculate new camera position
    const newPosition = new THREE.Vector3()
        .copy(targetPosition)
        .add(direction.multiplyScalar(-optimalDistance));
    
    // Ensure camera doesn't go below ground level
    newPosition.y = Math.max(newPosition.y, targetPosition.y + 50);
    
    return newPosition;
}

function zoomToDefault(controls, camera) {
    const { camPos, lookAt } = defaultCameraSettings();

    createTween(controls.current.target, lookAt, 2100);
    createTween(camera.position, camPos, 2000);
}

function createTween(target, to, duration) {
    new TWEEN.Tween(target)
        .to(
            {
                x: to.x,
                y: to.y,
                z: to.z,
            },
            duration
        )
        .easing(TWEEN.Easing.Quadratic.Out)
        .start();
}

